<template>
  <section class="login" :style="getLoginBackgroundStyle">
    <div>
        <div v-if="showLoginIssueAlert">
          <v-warning-message
            :class="{ 'zoomOut animated': animated }"
            :warning="alertMessageDetails"
            :html="false"
            :is-urgent="false"
            :show-close="true"
            @close="closeWarning()"
          />
        </div>
        <div v-if="showIEAlert">
          <v-warning-message
            :class="{ 'zoomOut animated': animated }"
            :warning="'Om te kunnen beschikken over de nieuwste functionaliteiten en beveiligingsupdates van ons voorschrijfsysteem, is het noodzakelijk dat u gebruik maakt van een andere webbrowser dan Internet Explorer 11. Microsoft is sinds 2021 gestopt met de ondersteuning van deze browser. <br><br>  Wij adviseren u gebruik te maken van een moderne browser. Google Chrome geeft de beste resultaten voor het gebruik van het systeem. Heeft u vragen over dit bericht? Neem contact op met de helpdesk van uw instelling of vraag uw rayonmanager.'"
            :html="true"
            :is-urgent="false"
          />
        </div>
      <username-password-login />
      <generic-modal v-if="showBrowserRelatedPopup" :has-close-button="true" @close="closeModal">
        <template #header />
        <template #body>
          <p>
            Mediq maakt gebruik van beveiligingsopties die mogelijk niet door uw browser worden ondersteund. Wij raden u
            aan om spoedig een recente versie van uw browser te installeren.
          </p>
        </template>
      </generic-modal>
    </div>
  </section>
</template>

<script>
import { ref, onBeforeMount, computed, watchEffect } from 'vue';
import { useBrowserDetect } from 'vue3-detect-browser';
import router from '@/router';
import GenericModal from '@/components/shared/GenericModal';
import UsernamePasswordLogin from './UsernamePasswordLogin';
import { isLoggedIn$, currentUserIdentity$ } from '@/services';
import VWarningMessage from '@/components/shared/WarningMessage';
import { combineLatest } from 'rxjs';
import { useStore, mapActions } from 'vuex';
import background1 from '@/assets/oneportal.jpg'; // default
import background2 from '@/assets/huisarts.jpg'; // Use only when GP portal

export default {
  name: 'LoginPage',
  components: {
    GenericModal,
    UsernamePasswordLogin,
    VWarningMessage
  },
  setup() {
    const store = useStore(); // Use useStore to access the store
    const showLoginIssueAlert = ref(false);
    const animated = ref(false);
    const timer = ref(null);
    const currentUserIdentity = ref(null);
    const isLoggedIn = ref(false);
    const isSecurityPolicyExpired = ref(false);

    const portalLogo = computed(() => store.getters['navigation/getPortalLogo']);
    const alertMessageDetails = computed(() => store.getters['navigation/getAlertMessageDetails']);
    const { isFirefox, isChrome, isIE, isEdge, isSafari, meta } = useBrowserDetect();
    const showBrowserRelatedPopup = computed(() => {
      let status = false;
      if (
        (isChrome && meta.version < 30) ||
        (isFirefox && meta.version < 27) ||
        (isIE && meta.version < 11) ||
        (isEdge && meta.version < 12) ||
        (isSafari && meta.version < 7)
      ) {
        status = true;
      }
      return status;
    });
    const redirectedFromRegistration = window.localStorage.getItem('redirectedFromRegistration');
    const showIEAlert = computed(() => {
      const ua = window.navigator.userAgent;
      const msie = ua.indexOf('MSIE ');
      const trident = ua.indexOf('Trident/');

      return (msie > 0 || trident > 0)
        ? true
        : false;
    });

    const subscriptions = () => {
      // currentUserIdentity subscription
      watchEffect(() => {
        currentUserIdentity.value = currentUserIdentity$.value;
      });

      // isLoggedIn subscription
      watchEffect(() => {
        isLoggedIn.value = isLoggedIn$.value;
      });
    };

    // Call subscriptions when component is mounted
    subscriptions();

    // Combined observable for result
    const result = combineLatest([isLoggedIn$, currentUserIdentity$]);

    currentUserIdentity$.subscribe((data) => {
      if (data && data.isSecurityPolicyExpired === true) {
        isSecurityPolicyExpired.value = true;
        router.push('/closeddepartment');
      }
    });
    // Subscribe to combined observable
    result.subscribe((data) => {
      if (data[0] === true && data[1] && isSecurityPolicyExpired.value !== true)  {
        const pendingNedapResolve = window.localStorage.getItem('nedapPendingResolve');
        if (pendingNedapResolve) {
          try {
            const { code, clientId } = JSON.parse(pendingNedapResolve);
            window.localStorage.removeItem('tokenlogin');
            router.push(`/koppeling/${code}/${clientId}`);
            return;
          } catch (error) {
            window.localStorage.removeItem('nedapPendingResolve');
          }
        }

        if (!redirectedFromRegistration) {
          router.push('/');
      } else {
        router.push('/collega-registreren');
      }
      }
    });

    const isInIframe = computed(() => {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    });

    onBeforeMount(() => {
      clearTimeout(timer.value);
      store.dispatch('navigation/fetchPortalLogo').then(() => {
      });
       store.dispatch('navigation/fetchWarning').then(() => {
        if (alertMessageDetails.value) {
          showLoginIssueAlert.value = true;
        }
      });
      timer.value = setTimeout(() => {
        window.location.replace(window.location.pathname + '?' + Math.random());
      }, 30 * 60 * 1000);
    });

    const { fetchPortalLogo } = mapActions('navigation', ['fetchPortalLogo']);

    const closeModal = () => {
      showBrowserRelatedPopup.value = false;
    };

    const closeOverlay = () => {
      showBrowserRelatedPopup.value = false;
    };

    const closeWarning = () => {
      animated.value = true;
      setTimeout(() => {
        animated.value = false;
        showLoginIssueAlert.value = false;
      }, 1000);
    };

    const getLoginBackgroundStyle = computed(() => {
      const url = window.location.href;
      if (isInIframe.value) {
        return { background: 'white' };
      } else {
        return {
          background: `linear-gradient(rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.4)),
                      linear-gradient(to bottom, transparent 0%, white 100%),
                      url(${url.includes('huisarts') ? background2 : background1}) center center no-repeat`
        };
      }
    });

    return {
      store,
      portalLogo,
      alertMessageDetails,
      showBrowserRelatedPopup,
      showIEAlert,
      showLoginIssueAlert,
      animated,
      closeModal,
      closeOverlay,
      closeWarning,
      fetchPortalLogo,
      currentUserIdentity,
      isLoggedIn,
      result,
      isInIframe,
      getLoginBackgroundStyle,
    };
  }
};
</script>

<style lang="scss" scoped>
.login {
  background-size: cover !important;
  min-height: 100vh;
  width: 100%;
  .logo {
    @include min-screen(grid-breakpoint(md)) {
      margin: 40px 10px;
    }

    img {
      display: block;
      height: 60px;
      margin: 0 auto;

      @include min-screen(grid-breakpoint(md)) {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }
  &__header {
    margin: 30px 10px 25px 10px;
  }
  &__closeOverlay {
    float: right;
    margin: -25px -15px 0px 0px;
    cursor: pointer;
  }
  legend {
    font-size: 16px;
    line-height: 1.5;
  }
  &__greyColor {
    fill: $gray !important;
  }
  &__greenColor {
    fill: $dark-green !important;
  }
  .btn {
    margin-bottom: 1rem;
    @include min-screen(grid-breakpoint(md)) {
      margin-bottom: 0;
    }
  }
  p {
    color: $gray-darkest;
  }
  > .row {
    margin-bottom: 0;
    height: 100vh;
  }
  .loginimage {
    height: 100%;
    width: 100%;
    background-size: cover !important;
  }
  :deep(.form-group-simple input::-ms-reveal) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
}
</style>
